import React, { useState, useEffect } from 'react';
import { DndContext, closestCorners, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { FulfillmentColumn as FulfillmentColumnType, Fulfillment, FulfillmentProject } from '@/types/fulfillment';
import { useFulfillmentData } from '@/hooks/projects/use-fulfillment-data';
import { useFulfillmentDrag } from '@/hooks/projects/use-fulfillment-drag';
import FulfillmentBoardContent from './FulfillmentBoardContent';
import FulfillmentBoardDetails from './FulfillmentBoardDetails';
import UpdatingNotification from '@/components/ui/UpdatingNotification';

interface FulfillmentBoardProps {
  initialColumns: FulfillmentColumnType[];
}

const FulfillmentBoard = ({ initialColumns }: FulfillmentBoardProps) => {
  const [columns, setColumns] = useState<FulfillmentColumnType[]>(initialColumns);
  const [selectedProject, setSelectedProject] = useState<Fulfillment | null>(null);
  const { data: latestData } = useFulfillmentData();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync columns whenever latestData changes
  useEffect(() => {
    if (latestData) {
      console.log('[FulfillmentBoard] Latest data received, updating columns');
      
      // Ensure the data is properly typed as FulfillmentProject[]
      const typedProjects = latestData as unknown as FulfillmentProject[];
      
      const updatedColumns = initialColumns.map(column => ({
        ...column,
        projects: typedProjects.filter(p => p.status === column.id)
      }));
      
      console.log('[FulfillmentBoard] Updated columns structure:', updatedColumns);
      setColumns(updatedColumns);
    }
  }, [latestData, initialColumns]);

  const {
    activeProject,
    isUpdating,
    handleDragStart,
    handleDragOver,
    handleDragEnd
  } = useFulfillmentDrag(columns, setColumns);

  const handleProjectClick = (project: Fulfillment) => {
    console.log(`[Project Click] Selected project:`, project);
    setSelectedProject(project);
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <FulfillmentBoardContent
          columns={columns}
          activeProject={activeProject}
          onProjectClick={handleProjectClick}
        />
      </DndContext>

      <FulfillmentBoardDetails
        selectedProject={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
      
      <UpdatingNotification 
        isVisible={isUpdating} 
        message="Updating" 
        status="updating"
        position="bottom-left"
      />
    </>
  );
};

export default FulfillmentBoard;
